import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Api } from 'src/models/Api.model';
import { loadSessionsWithExtraData } from 'src/models/GalenData/helpers/loadSessionsWithExtraData';
import { SortOrder } from 'src/models/GalenData/SortOrder.model';
import { I18NextNS } from 'src/models/Language.model';
import { Session } from 'src/models/Session.model';

import useGetRetinalExamDataModelId from 'src/hooks/useGetRetinalExamDataModelConfig';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

const useLoadSessions = () => {
  const { handleHttpRequestError } = useHandleHttpRequestError();
  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);
  const [currentSearchValue, setCurrentSearchValue] = useState<string>('');

  const [currentPage, setCurrentPage] = useState<number>(1);

  const handleSetCurrentPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSetCurrentSearchValue = useCallback((searchValue: string) => {
    setCurrentSearchValue(searchValue);
  }, []);

  const [isLoading, setIsLoading] = useState(false);

  const { deviceDataModelId } = useGetRetinalExamDataModelId();

  const [dataResource, setDataResource] = useState<{
    sessions: Session[];
    totalElements: number;
    totalPages: number;
  }>({
    sessions: [],
    totalElements: 0,
    totalPages: 0,
  });

  const handleLoadSessions = useCallback(
    async (
      page: number,
      pageSize: number,
      searchValue: string,
      sessionsSortOrder: SortOrder,
      siteId?: string,
    ) => {
      try {
        setIsLoading(true);

        const res = await loadSessionsWithExtraData(
          deviceDataModelId,
          page,
          pageSize,
          searchValue,
          sessionsSortOrder,
          siteId,
        );

        setDataResource(res);
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);

        Api.alertBox({
          title: t('bootbox.title.Error'),
          message:
            t('message.error.failedToLoadSessions', {
              ns: I18NextNS.COMMON,
            }) +
            ' ' +
            errorMessage,

          locale: language,
          type: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [deviceDataModelId, handleHttpRequestError, language, t],
  );

  return {
    currentPage,
    currentSearchValue,
    handleSetCurrentPage,
    handleSetCurrentSearchValue,
    isLoading,
    dataResource,
    handleLoadSessions,
  };
};

export default useLoadSessions;
